import { useMemo, useState, type KeyboardEvent } from "react";
import { CreditCard, ExternalLink, PlugZap, Search, ShieldCheck, SlidersHorizontal } from "lucide-react";
import { trackBlogComparisonRiskProfile } from "@/lib/analytics";

type Lens = "all" | "authority" | "data" | "review";
type RiskProfile = "research_only" | "connected_apps" | "payments";

type Cell = {
  summary: string;
  detail: string;
  state: "documented" | "partial" | "not-stated";
};

type ComparisonRow = {
  lens: Exclude<Lens, "all">;
  riskProfiles: RiskProfile[];
  control: string;
  whyItMatters: string;
  instinct: Cell;
  muse: Cell;
  chatgpt: Cell;
};

const PROVIDERS = [
  {
    id: "instinct",
    name: "Instinct",
    sourceLabel: "Privacy policy",
    sourceUrl: "https://instinct.com/privacy-policy",
  },
  {
    id: "muse",
    name: "Muse",
    sourceLabel: "Meta announcement",
    sourceUrl: "https://about.fb.com/news/2026/09/introducing-muse-personal-ai-agent/",
  },
  {
    id: "chatgpt",
    name: "ChatGPT agent",
    sourceLabel: "OpenAI announcement",
    sourceUrl: "https://openai.com/index/introducing-chatgpt-agent/",
  },
] as const;

const LENSES: { id: Lens; label: string; description: string }[] = [
  { id: "all", label: "All controls", description: "Compare authority, data and review evidence." },
  { id: "authority", label: "Authority", description: "What is stated about permissions and consequential actions?" },
  { id: "data", label: "Data & credentials", description: "What is stated about access, retention and model use?" },
  { id: "review", label: "Review & exit", description: "What can the user inspect, stop, revoke or delete?" },
];

const RISK_PROFILES: {
  id: RiskProfile;
  label: string;
  description: string;
  focus: string;
  icon: typeof Search;
}[] = [
  {
    id: "research_only",
    label: "Research only",
    description: "No connected accounts, messages, calendar access, payments or purchases.",
    focus: "Prioritise clear purpose, data choices and a reliable stop or deletion path before expanding access.",
    icon: Search,
  },
  {
    id: "connected_apps",
    label: "Connected apps",
    description: "Delegated access to services such as email, calendar, documents or messaging-without payment authority.",
    focus: "Prioritise granular permissions, credentials boundaries, data handling and the ability to revoke access.",
    icon: PlugZap,
  },
  {
    id: "payments",
    label: "Payments",
    description: "The agent may book, buy, send or otherwise initiate consequential commercial activity.",
    focus: "Prioritise explicit approval, payment and credential limits, review records, and an immediate stop path.",
    icon: CreditCard,
  },
];

const ROWS: ComparisonRow[] = [
  {
    lens: "authority",
    riskProfiles: ["research_only", "connected_apps", "payments"],
    control: "Permission scope",
    whyItMatters: "A useful agent needs access, but a user needs to know what each connected service permits.",
    instinct: {
      summary: "Permission-dependent access",
      detail: "Its policy says access depends on permissions a user grants, including connected accounts and private materials. The policy does not itself describe a universal per-action permission interface.",
      state: "partial",
    },
    muse: {
      summary: "App-level access choices",
      detail: "Meta says people choose which apps connect and how much access Muse receives; its example distinguishes reading email from also sending it.",
      state: "documented",
    },
    chatgpt: {
      summary: "Task and browser controls",
      detail: "OpenAI says users can interrupt, stop tasks or take over the browser, while the agent requests permission before consequential actions.",
      state: "documented",
    },
  },
  {
    lens: "authority",
    riskProfiles: ["connected_apps", "payments"],
    control: "Consequential actions",
    whyItMatters: "Booking, sending and paying create a different risk class from drafting or research.",
    instinct: {
      summary: "Independent-action warning",
      detail: "The policy warns that an autonomous assistant may act independently and may produce unintended payments or communications. It encourages users to review actions; this source does not promise a universal confirmation step.",
      state: "partial",
    },
    muse: {
      summary: "Approval before selected actions",
      detail: "Meta says Muse requests permission when needed and checks before sensitive actions such as sending email or making a purchase.",
      state: "documented",
    },
    chatgpt: {
      summary: "Confirmation and watch mode",
      detail: "OpenAI says ChatGPT agent requests explicit confirmation for real-world consequences and requires active oversight for certain critical tasks, including sending email.",
      state: "documented",
    },
  },
  {
    lens: "data",
    riskProfiles: ["connected_apps", "payments"],
    control: "Connected-app credentials",
    whyItMatters: "Connected services can expose documents, messages and session access, even when no payment authority is granted.",
    instinct: {
      summary: "May receive supplied credentials",
      detail: "Its policy says a user may provide payment information or third-party credentials for tasks. The sources reviewed do not establish independent assurance for the implementation of those controls.",
      state: "partial",
    },
    muse: {
      summary: "Secure-storage claim",
      detail: "Meta says Muse does not see passwords or payment methods, uses secure storage and can use a one-time Link card for eligible purchases. These are company statements.",
      state: "documented",
    },
    chatgpt: {
      summary: "Private browser takeover inputs",
      detail: "OpenAI says inputs entered during browser takeover, such as passwords, are not collected or stored by ChatGPT. Website cookies may persist under each site’s policy.",
      state: "documented",
    },
  },
  {
    lens: "authority",
    riskProfiles: ["payments"],
    control: "Payment authority",
    whyItMatters: "A purchase or payment creates a direct financial consequence, so the user needs a clearly documented approval and reversal path.",
    instinct: {
      summary: "Independent-action warning",
      detail: "Its policy warns that the assistant may act independently and could produce unintended payments. The cited policy encourages review but does not promise a universal payment-confirmation control.",
      state: "partial",
    },
    muse: {
      summary: "Purchase permission and one-time card",
      detail: "Meta says Muse checks before selected sensitive actions, including purchases, and describes a one-time Link card for eligible purchases. These are company statements.",
      state: "documented",
    },
    chatgpt: {
      summary: "Explicit consequential-action confirmation",
      detail: "OpenAI says ChatGPT agent asks for explicit confirmation for real-world consequences. The cited material should be checked against the live task flow before delegating payment authority.",
      state: "documented",
    },
  },
  {
    lens: "data",
    riskProfiles: ["research_only", "connected_apps", "payments"],
    control: "Model-use and data choices",
    whyItMatters: "A consent setting can be meaningful only when users understand exceptions, connected-data limits and retention behaviour.",
    instinct: {
      summary: "Training opt-out with stated limits",
      detail: "Its policy offers a settings opt-out from some model-training use, with stated safety-review exceptions; it separately says Google Workspace data is not used for model training or ads.",
      state: "documented",
    },
    muse: {
      summary: "Training opt-out and ad boundary",
      detail: "Meta says users can opt out of interaction training and that conversations and VM data are not shared with Meta’s ad systems.",
      state: "documented",
    },
    chatgpt: {
      summary: "Browsing and connector controls",
      detail: "OpenAI describes one-click deletion of browsing data and log-out of browser sessions. Its safety guidance recommends disabling connectors when they are not needed.",
      state: "documented",
    },
  },
  {
    lens: "review",
    riskProfiles: ["research_only", "connected_apps", "payments"],
    control: "Review, stop and exit",
    whyItMatters: "A person should be able to identify what happened, reduce authority and correct a mistake.",
    instinct: {
      summary: "Disconnect and deletion routes",
      detail: "Its policy describes revoking Google Workspace access, disconnecting integrations and deleting indexed external-source data. Disconnecting alone does not automatically delete data already collected.",
      state: "documented",
    },
    muse: {
      summary: "Audit-trail claim",
      detail: "Meta says Muse shows a complete audit trail of what it has done and plans to do; users can change access, disconnect services or ask it to forget specific memories.",
      state: "documented",
    },
    chatgpt: {
      summary: "Interrupt or take over",
      detail: "OpenAI says users can interrupt, stop a task or take over the browser. The cited launch material does not describe a universal, exportable action-receipt feature.",
      state: "partial",
    },
  },
];

const STATE_STYLE: Record<Cell["state"], { label: string; className: string }> = {
  documented: { label: "Publicly stated", className: "border-emerald-300/25 bg-emerald-400/10 text-emerald-200" },
  partial: { label: "Partly documented", className: "border-amber-300/25 bg-amber-400/10 text-amber-100" },
  "not-stated": { label: "Not established", className: "border-slate-300/20 bg-slate-400/10 text-slate-200" },
};

function ControlCell({ cell }: { cell: Cell }) {
  const state = STATE_STYLE[cell.state];
  return (
    <div className="min-w-[15rem] space-y-2">
      <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.13em] ${state.className}`}>
        {state.label}
      </span>
      <p className="font-medium leading-snug text-slate-100">{cell.summary}</p>
      <p className="text-sm leading-relaxed text-slate-300/85">{cell.detail}</p>
    </div>
  );
}

/**
 * Source-qualified comparison of publicly stated user controls. It deliberately
 * does not score vendors, imply independent security assurance, or fill in
 * controls that the cited public materials do not establish.
 */
export default function PersonalAgentControlsComparison() {
  const [lens, setLens] = useState<Lens>("all");
  const [riskProfile, setRiskProfile] = useState<RiskProfile>("research_only");
  const rows = useMemo(
    () => ROWS.filter((row) => (lens === "all" || row.lens === lens) && row.riskProfiles.includes(riskProfile)),
    [lens, riskProfile],
  );
  const activeLens = LENSES.find((item) => item.id === lens)!;
  const activeRiskProfile = RISK_PROFILES.find((item) => item.id === riskProfile)!;

  function chooseRiskProfile(profile: RiskProfile) {
    setRiskProfile(profile);
    trackBlogComparisonRiskProfile({
      post_slug: "instinct-personal-agent-trust-alignment-2026",
      comparison_name: "personal_agent_controls",
      risk_profile: profile,
    });
  }

  function handleRiskProfileKeyDown(event: KeyboardEvent<HTMLButtonElement>, profile: RiskProfile) {
    const currentIndex = RISK_PROFILES.findIndex((item) => item.id === profile);
    let nextIndex: number | null = null;

    if (event.key === "ArrowRight" || event.key === "ArrowDown") nextIndex = (currentIndex + 1) % RISK_PROFILES.length;
    if (event.key === "ArrowLeft" || event.key === "ArrowUp") nextIndex = (currentIndex - 1 + RISK_PROFILES.length) % RISK_PROFILES.length;
    if (event.key === "Home") nextIndex = 0;
    if (event.key === "End") nextIndex = RISK_PROFILES.length - 1;
    if (nextIndex === null) return;

    event.preventDefault();
    const nextProfile = RISK_PROFILES[nextIndex];
    chooseRiskProfile(nextProfile.id);
    const controls = event.currentTarget.closest('[role="radiogroup"]')?.querySelectorAll<HTMLButtonElement>('[role="radio"]');
    controls?.[nextIndex]?.focus();
  }

  return (
    <section
      aria-labelledby="personal-agent-controls-heading"
      className="my-12 overflow-hidden rounded-2xl border border-cyan-400/20 bg-gradient-to-br from-slate-950 via-slate-900 to-cyan-950/25 shadow-xl shadow-cyan-950/20"
    >
      <div className="border-b border-white/10 px-5 py-6 sm:px-7 sm:py-8">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-cyan-200">
              <ShieldCheck className="h-4 w-4" aria-hidden="true" />
              Dynamic control comparison
            </p>
            <h2 id="personal-agent-controls-heading" className="mt-3 font-serif text-2xl leading-tight text-white sm:text-3xl">
              Instinct vs Muse vs ChatGPT agent: what do the public controls say?
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-300">
              Filter by the control you are assessing. This is a documentation comparison, not a security ranking or independent certification; product behaviour and availability can change.
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs text-cyan-100/80">
            <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
            Updated from cited public materials on 30 September 2026
          </div>
        </div>

        <fieldset className="mt-7">
          <legend className="text-sm font-semibold text-white">Choose the delegation risk profile</legend>
          <p className="mt-1 text-sm leading-relaxed text-slate-300/80">Select the most consequential access you would consider giving an agent. The evidence below narrows to that delegation level.</p>
          <div className="mt-4 grid gap-3 md:grid-cols-3" role="radiogroup" aria-label="Choose the delegation risk profile">
            {RISK_PROFILES.map((item) => {
              const active = item.id === riskProfile;
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  tabIndex={active ? 0 : -1}
                  onClick={() => chooseRiskProfile(item.id)}
                  onKeyDown={(event) => handleRiskProfileKeyDown(event, item.id)}
                  className={`rounded-xl border p-4 text-left transition-colors ${active
                    ? "border-cyan-300 bg-cyan-300/15 shadow-sm shadow-cyan-400/10"
                    : "border-white/15 bg-white/[0.035] hover:border-cyan-300/50 hover:bg-cyan-300/10"}`}
                >
                  <span className="flex items-center gap-2 font-semibold text-white"><Icon className="h-4 w-4 text-cyan-200" aria-hidden="true" />{item.label}</span>
                  <span className="mt-2 block text-sm leading-relaxed text-slate-300">{item.description}</span>
                </button>
              );
            })}
          </div>
        </fieldset>

        <div className="mt-6 flex flex-wrap gap-2" role="tablist" aria-label="Filter the personal-agent controls comparison">
          {LENSES.map((item) => {
            const active = item.id === lens;
            return (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setLens(item.id)}
                className={`rounded-full border px-3 py-2 text-sm font-semibold transition-colors ${active
                  ? "border-cyan-300 bg-cyan-300 text-slate-950"
                  : "border-white/15 bg-white/[0.04] text-slate-200 hover:border-cyan-300/50 hover:bg-cyan-300/10"}`}
              >
                {item.label}
              </button>
            );
          })}
        </div>
        <p className="mt-3 text-sm leading-relaxed text-slate-300/80" role="status">
          <span className="font-medium text-cyan-100">{activeRiskProfile.label}:</span> {activeRiskProfile.focus} <span className="text-slate-400">{activeLens.description}</span>
        </p>
      </div>

      <div className="overflow-x-auto px-3 py-4 sm:px-5 sm:py-6">
        <table className="w-full min-w-[72rem] border-separate border-spacing-0 text-left">
          <caption className="sr-only">Comparison of publicly documented controls for Instinct, Muse and ChatGPT agent.</caption>
          <thead>
            <tr>
              <th scope="col" className="sticky left-0 z-10 w-[16rem] border-b border-white/10 bg-slate-950 px-4 py-4 text-xs font-semibold uppercase tracking-[0.13em] text-slate-300">
                Control to assess
              </th>
              {PROVIDERS.map((provider) => (
                <th key={provider.id} scope="col" className="min-w-[15rem] border-b border-l border-white/10 bg-slate-950/80 px-4 py-4 align-top">
                  <a
                    href={provider.sourceUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="group inline-flex items-center gap-1.5 font-serif text-lg text-white transition-colors hover:text-cyan-200"
                  >
                    {provider.name}
                    <ExternalLink className="h-3.5 w-3.5 opacity-70 group-hover:opacity-100" aria-hidden="true" />
                  </a>
                  <span className="mt-1 block text-xs font-normal text-slate-400">{provider.sourceLabel}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.control} className="align-top">
                <th scope="row" className="sticky left-0 z-10 border-b border-white/10 bg-slate-950 px-4 py-5">
                  <p className="font-semibold text-white">{row.control}</p>
                  <p className="mt-2 text-sm font-normal leading-relaxed text-slate-400">{row.whyItMatters}</p>
                </th>
                <td className="border-b border-l border-white/10 bg-white/[0.02] px-4 py-5"><ControlCell cell={row.instinct} /></td>
                <td className="border-b border-l border-white/10 bg-white/[0.02] px-4 py-5"><ControlCell cell={row.muse} /></td>
                <td className="border-b border-l border-white/10 bg-white/[0.02] px-4 py-5"><ControlCell cell={row.chatgpt} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="border-t border-white/10 bg-slate-950/70 px-5 py-5 text-sm leading-relaxed text-slate-300 sm:px-7">
        <strong className="text-white">Practical takeaway:</strong> start with low-risk permissions, require approval for consequential tasks, keep payments and credentials bounded, and test the stop, revoke and dispute path before delegating more authority.
      </div>
    </section>
  );
}
